// Pure, environment-neutral resize core — no Worker/window/`self` references, so
// it runs identically inside the resize Web Worker (src/lib/image/resizeImage.worker.ts)
// and as the main-thread fallback (src/lib/image/resizeImage.ts).
//
// Given a JPEG File it downscales the long edge to `maxEdge` and re-encodes to
// JPEG at `quality`, RETAINING the original's EXIF metadata via piexifjs with two
// crucial corrections: Orientation is reset to 1 (pixels are drawn upright — decoded upright, or the
// orientation applied while drawing: exifOrientation.ts) and the Pixel X/Y
// dimensions are updated to the new size; the embedded EXIF thumbnail (1st IFD) is
// dropped so it can't disprove the new orientation/size.
//
// Robustness (the feature must NEVER lose a file):
//   • non-JPEG                       → returns null  (caller keeps the original)
//   • already ≤ maxEdge              → returns null  (keeps original, full metadata)
//   • no decode route / no OffscreenCanvas / no JPEG encoder → throws ResizeUnsupportedError (the caller
//     decides: a resize preset keeps the original; a preview-first copy fails visibly instead)
//   • EXIF parse/insert throws       → returns the resized JPEG WITHOUT EXIF
import piexif from "piexifjs";
import { decodeDrawable, pickScale, readJpegHeader } from "./scaledDecode";
import { drawUpright, uprightSize, type Drawable } from "./exifOrientation";

export interface ResizeOpts {
  /** Target maximum for the LONG edge, in pixels. Never upscales. */
  maxEdge: number;
  /** JPEG quality, 0..1. */
  quality: number;
}

const CHUNK = 0x8000;
const EXIF_HEAD_BYTES = 256 * 1024;

/** Blob → Latin-1 binary string (what piexifjs operates on). Chunked to avoid
 *  blowing the call stack with `String.fromCharCode.apply` on large buffers. */
async function blobToBinaryString(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let out = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return out;
}

/** Latin-1 binary string → Blob of the given MIME type. */
function binaryStringToBlob(bin: string, type: string): Blob {
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type });
}

/** Thrown when this browser can't make the copy (no OffscreenCanvas, no decode route, an encoder that
 *  won't write JPEG) — unlike `null` ("keep the original": already small / not a JPEG). A preview-first
 *  copy then fails visibly; it is never replaced by the original (resizeImage.resizeForCopy). */
export class ResizeUnsupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ResizeUnsupportedError";
  }
}

export const isResizeUnsupported = (err: unknown): boolean =>
  err instanceof ResizeUnsupportedError || (!!err && (err as { name?: string }).name === "ResizeUnsupportedError");

/**
 * Resize+re-encode a JPEG File, preserving EXIF.
 * @returns a JPEG Blob, or `null` when the original should be kept as-is (not a JPEG, already fits).
 * @throws ResizeUnsupportedError when this context can't decode / draw / encode it (see above).
 */
export async function resizeFileToBlob(
  file: File,
  { maxEdge, quality }: ResizeOpts,
): Promise<Blob | null> {
  // Only ever touch real JPEGs; anything else is returned untouched by the caller.
  if (file.type !== "image/jpeg") return null;
  if (typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") {
    throw new ResizeUnsupportedError("this browser has no OffscreenCanvas / createImageBitmap");
  }

  // 1. Read the ORIGINAL file's EXIF FIRST (before any re-encode). Best-effort.
  // EXIF lives in an APP1 segment near the start (≤ 64 KB), so read only the file's head instead of
  // turning the whole 10–25 MB original into a string; fall back to the whole file if the head
  // doesn't parse (unusual segment order).
  let exif: ReturnType<typeof piexif.load> | null = null;
  try {
    exif = piexif.load(await blobToBinaryString(file.slice(0, EXIF_HEAD_BYTES)));
  } catch {
    try {
      exif = piexif.load(await blobToBinaryString(file));
    } catch {
      exif = null; // no/!parseable EXIF — we'll still resize, just without metadata
    }
  }

  // 2. Decode — only as large as the output needs where the browser can (a 1200px copy of a 24MP original
  // needs a 1/4 decode, which JPEG gives almost for free: scaledDecode.ts), else in full. Upright, or the
  // stored pixels with the EXIF orientation applied while drawing (WebKit), or via an <img> on the page.
  const header = await readJpegHeader(file);
  if (header && Math.max(header.width, header.height) <= maxEdge) return null; // fits already — keep it
  let img: Drawable | null = null;
  try {
    const fraction = header ? maxEdge / Math.max(header.width, header.height) : 1;
    img = await decodeDrawable(file, header, pickScale(fraction, header));
  } catch {
    img = null;
  }
  if (!img) throw new ResizeUnsupportedError("no decode route for this JPEG here");

  // The FULL upright size (a scaled decode is a fraction of it).
  const decoded = uprightSize(img);
  const scaledDecode = !!header && decoded.width < header.width - 1;
  const origW = header && scaledDecode ? header.width : decoded.width;
  const origH = header && scaledDecode ? header.height : decoded.height;
  const longEdge = Math.max(origW, origH);
  // Never upscale: if it already fits, prefer the untouched original (avoids
  // needless quality loss and preserves full metadata, incl. ICC + thumbnail).
  if (longEdge <= maxEdge) {
    img.close();
    return null;
  }

  const scale = maxEdge / longEdge;
  const w = Math.max(1, Math.round(origW * scale));
  const h = Math.max(1, Math.round(origH * scale));

  // 3. Draw (upright) + encode.
  let blob: Blob;
  try {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    drawUpright(ctx, img, w, h);
    blob = await canvas.convertToBlob({ type: "image/jpeg", quality });
  } catch (err) {
    throw new ResizeUnsupportedError(`draw/encode failed: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    img.close();
  }
  // An encoder that doesn't do JPEG hands back a PNG instead (the spec's fallback) — several times the
  // size, and not what the server keys as a .jpg. Never upload that as the copy.
  if (blob.type !== "image/jpeg") throw new ResizeUnsupportedError(`the canvas encoder made ${blob.type || "an unknown type"}, not JPEG`);

  // 4. Re-inject the original EXIF, corrected for the new upright/resized pixels.
  if (!exif) return blob;
  try {
    exif["0th"][piexif.ImageIFD.Orientation] = 1; // pixels are already upright
    exif.Exif[piexif.ExifIFD.PixelXDimension] = w;
    exif.Exif[piexif.ExifIFD.PixelYDimension] = h;
    // Drop the embedded thumbnail (1st IFD) — it still shows the old size/orientation.
    exif["1st"] = {};
    exif.thumbnail = null;
    const exifBytes = piexif.dump(exif);
    const resizedBinary = await blobToBinaryString(blob);
    const withExif = piexif.insert(exifBytes, resizedBinary);
    return binaryStringToBlob(withExif, "image/jpeg");
  } catch {
    return blob; // EXIF re-inject failed → resized JPEG without EXIF (never fail)
  }
}
